// Ported verbatim from the LeadMailer Design System (components/data/Table.jsx).
// Responsive: a real <table> when wide, stacked cards under `stackBelow` px.
import React from 'react'

export interface Column<T> {
  key: string
  label: React.ReactNode
  align?: 'left' | 'right' | 'center'
  nowrap?: boolean
  render?: (row: T) => React.ReactNode
}

export interface TableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey?: keyof T & string
  stackBelow?: number
  onRowClick?: (row: T) => void
  style?: React.CSSProperties
}

export function Table<T extends Record<string, any>>({
  columns = [],
  rows = [],
  rowKey,
  stackBelow = 640,
  onRowClick,
  style,
}: TableProps<T>) {
  const [narrow, setNarrow] = React.useState(false)

  React.useEffect(() => {
    const m = window.matchMedia(`(max-width:${stackBelow}px)`)
    const f = () => setNarrow(m.matches)
    f()
    m.addEventListener('change', f)
    return () => m.removeEventListener('change', f)
  }, [stackBelow])

  const key = (r: T, i: number) => (rowKey ? String(r[rowKey]) : String(i))

  if (narrow) {
    // Flat stacked rows: hairline-separated, flush with the content edge.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', ...style }}>
        {rows.map((r, i) => (
          <div
            key={key(r, i)}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={{
              borderBottom: '1px solid var(--border-default)',
              padding: '10px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              cursor: onRowClick ? 'pointer' : 'default',
            }}
          >
            {columns.map((c) => (
              <div
                key={c.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-wide)',
                  }}
                >
                  {c.label}
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-primary)',
                    textAlign: 'right',
                    minWidth: 0,
                  }}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <table
      style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)', ...style }}
    >
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th
              key={c.key}
              style={{
                textAlign: c.align || 'left',
                // flush: first/last columns align with the content edges
                padding: `6px ${i === columns.length - 1 ? 0 : 12}px 6px ${i === 0 ? 0 : 12}px`,
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-wide)',
                borderBottom: '1px solid var(--glass-edge)',
                whiteSpace: 'nowrap',
                // Accent header strip. The tint is what ties Send logs, Leads,
                // Queue and the suppression list to the dashboard's panels; it
                // reads as one system rather than four bare tables.
                background: 'var(--surface-box-header)',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <TR
            key={key(r, i)}
            row={r}
            columns={columns}
            onRowClick={onRowClick}
            last={i === rows.length - 1}
            index={i}
          />
        ))}
      </tbody>
    </table>
  )
}

function TR<T extends Record<string, any>>({
  row,
  columns,
  onRowClick,
  last,
  index,
}: {
  row: T
  columns: Column<T>[]
  onRowClick?: (row: T) => void
  last: boolean
  index: number
}) {
  const [hover, setHover] = React.useState(false)
  // Zebra always, indigo on hover. Hover used to be reserved for clickable
  // tables, which left the dense read-only ones (Send logs, suppression) with no
  // row tracking at all — the thing you actually need at 12px across 8 columns.
  const background = hover
    ? 'var(--accent-soft)'
    : index % 2 === 1
      ? 'var(--surface-row-alt)'
      : 'transparent'
  return (
    <tr
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background,
        cursor: onRowClick ? 'pointer' : 'default',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
    >
      {columns.map((c, i) => (
        <td
          key={c.key}
          style={{
            padding: `8px ${i === columns.length - 1 ? 0 : 12}px 8px ${i === 0 ? 0 : 12}px`,
            textAlign: c.align || 'left',
            color: 'var(--text-primary)',
            borderBottom: last ? 'none' : '1px solid var(--border-default)',
            whiteSpace: c.nowrap ? 'nowrap' : 'normal',
          }}
        >
          {c.render ? c.render(row) : row[c.key]}
        </td>
      ))}
    </tr>
  )
}
